import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import './ApplicationStatusForm.css';
import { ApplicationStatus, ApplicationStatusResponse } from '../../types/responses/ApplicationStatus';
import { Button } from '../../types/shared/Button';
import { dynamicRequest } from '../../utils/api-utils/api-utils';
import { usePushdownContext } from '../../providers/Pushdown/PushdownProvider';
import { getSecChildren } from '../PageSection/PageSection';
import withAsyncImport from '../../withAsyncImport';
import SessionStorage from '../../utils/SessionStorage';
import useSessionTimeout from '../../hooks/useSessionTimeout';
import { checkExistsDiacriticSpecialCharacters, regexValidation } from '../../utils/cob-utils';
import { transformHTMLParserLinks } from '../../utils/rewriteLink/rewriteLink';
import NameField from '../FieldContainers/NameField/NameField';
import SsnField from '../FieldContainers/SsnField/SsnField';
import { IApplicationStatusFormProps } from './shared/interfaces';
import { Row, Col, Title, Button as CLButton } from '../../importCL';
import { isFundingToggled } from '../../utils/acquisition-utils';
import { callAcquisitionBff } from '../../utils/acquisition-api/AcquisitionBff';
import { NewApplicationStatusResponse } from '../../types/responses/NewApplicationStatus';
import { ApplicationStatusMapper } from '../../utils/ApplicationStatusMapper';
import axios from 'axios';
import { generateErrorMessage } from '../../utils/error-message';

const StatusTemplate = withAsyncImport(() => import('./templates/StatusTemplate'));
const CobGrid = withAsyncImport(() => import('../CobGrid/CobGrid'));

export const ApplicationStatusFormEditConfig = {
    emptyLabel: 'ApplicationStatusForm',
    isEmpty: (props: PropsWithChildren<IApplicationStatusFormProps>) => {
        return !props;
    },
};

const abbreviateDate = (date: string) => {
    const tmp = new Date(date);

    const month = tmp.getUTCMonth() + 1;
    const day = tmp.getUTCDate();

    const monthString = month.toString().length > 1 ? month.toString() : '0' + month.toString();
    const dayString = day.toString().length > 1 ? day.toString() : '0' + day.toString();

    return monthString + '/' + dayString + '/' + tmp.getUTCFullYear();
};

const ApplicationStatusForm = (props: PropsWithChildren<IApplicationStatusFormProps>): JSX.Element => {
    const { showPushdown, closePushdown } = usePushdownContext();
    const [appInputs, setAppInputs] = useState({ ssnInput: '', lastNameInput: '' });
    const [appErrs, setAppErrs] = useState({ ssnError: false, lastNameError: false });
    const [cardList, setCardList] = useState<ApplicationStatus[]>([]);
    const [filteredCards, setFilteredCards] = useState<JSX.Element[]>([]);
    const { missingInfo, creditProtectionInfo } = props;
    const [isQuerying, setIsQuerying] = useState(false);

    const ssnRef = useRef<HTMLInputElement>();

    const regex = regexValidation();

    const checkEmpty = (value: string) => {
        return value === undefined || value === null || value.length <= 0;
    };

    const hasLastNameErrors = (value: string): boolean => {
        const isValid = value &&
            regex.allow.last_name.test(value) &&
            !regex.disallow.last_name.test(value) &&
            !checkExistsDiacriticSpecialCharacters(value);

        setAppErrs(prev => ({ ...prev, lastNameError: !isValid }));
        return !isValid;
    };

    const hasSsnErrors = (value: string): boolean => {
        const formattedSsn = value.replace(/[^0-9]/g, '');
        const repeatingPattern = /^0{3}\d{6}|\d{3}0{2}\d{4}|\d{5}0{4}$/;
        const missingVal = checkEmpty(formattedSsn) || formattedSsn.length < 9;
        const isValid = !missingVal &&
            regex.allow.ssn.test(formattedSsn) &&
            !regex.disallow.ssn.test(formattedSsn) &&
            !repeatingPattern.test(formattedSsn);

        setAppErrs(prev => ({ ...prev, ssnError: !isValid }));
        return !isValid;
    };

    const callNoStatus = () => {
        showPushdown(props.noStatusErrorCode!);
        setAppErrs({ ssnError: true, lastNameError: true });
    };

    const fetchCardName = async (reservationNumber: string): Promise<string> => {
        if (!reservationNumber?.length) {
            return null;
        }

        const programCode = reservationNumber.substring(0, 4);

        try {
            const results = await axios.get(`/services/CardNameContentFragment.programCode=${programCode}`);
            return results.data.cardName;
        } catch (err) {
            console.debug('Unable to retrieve card name: ', err);
        }

        return null;
    };

    const queryForStatus = async () => {

        const getArrayOfAppIds = (dataObj: any) => {
            let appIdObj;
            const resultObj = JSON.parse(dataObj);
            const appIdArr: any[] = [];
            for (let i = 0; i < resultObj.length; i++) {
                appIdObj = { ApplicationID: resultObj[i].ApplicationId };
                appIdArr.push(appIdObj);
            }
            return JSON.stringify(appIdArr);
        };

        const onSuccess = async (data: ApplicationStatusResponse) => {
            if (data && data.ApplicationStatus && data.ApplicationStatus.length) {
                SessionStorage.set('Result', JSON.stringify(data.ApplicationStatus));
                SessionStorage.set('BearerToken', JSON.stringify(data.BearerToken));
                if (sessionStorage.Result) {
                    if (JSON.parse(sessionStorage.Result).length !== -1) {
                        closePushdown();

                        const statuses: ApplicationStatus[] = data.ApplicationStatus;

                        for (let i = 0; i < statuses.length; i++) {
                            const newCardName = await fetchCardName(statuses[i].ReservationNumber);

                            if (newCardName !== null && newCardName !== undefined) {
                                statuses[i].ProductName = newCardName.toString();
                            }
                        }

                        setCardList(statuses);

                        if (data.ApplicationStatus.length > 1) {
                            const appIdArr = getArrayOfAppIds(sessionStorage.Result);
                            SessionStorage.set('AppIdArr', appIdArr);
                        } else {
                            SessionStorage.set(
                                'ApplicationId',
                                JSON.parse(sessionStorage.Result)[0].ApplicationId,
                            );
                        }
                    }
                }
            } else {
                callNoStatus();
            }
        };

        const onNewApiSuccess = async (response: NewApplicationStatusResponse) => {
            if (
                response &&
                response.applications &&
                response.applications.length
            ) {

                const mappedData = ApplicationStatusMapper.mapNewToOldFormat(response);

                SessionStorage.set(
                    'Result',
                    JSON.stringify(mappedData.ApplicationStatus),
                );

                if (sessionStorage.Result) {
                    if (JSON.parse(sessionStorage.Result).length !== -1) {
                        closePushdown();

                        const statuses: ApplicationStatus[] = mappedData.ApplicationStatus;

                        for (let i = 0; i < statuses.length; i++) {
                            const newCardName = await fetchCardName(statuses[i].ReservationNumber);

                            if (newCardName !== null && newCardName !== undefined) {
                                statuses[i].ProductName = newCardName.toString();
                            }
                        }

                        setCardList(statuses);
                    }
                }
            } else {
                callNoStatus();
            }
        };

        const onError = () => {
            showPushdown(props.apiErrorCode!);
            setAppErrs({ ssnError: true, lastNameError: true });
        };

        const handlePushdowns = () => {
            const errList: string[] = [];
            const lastRegex = hasLastNameErrors(appInputs.lastNameInput);
            const ssnRegex = hasSsnErrors(appInputs.ssnInput);

            if (lastRegex) {
                appErrs.lastNameError = true;
                errList.push(generateErrorMessage('<a href="#lastName-field-container">Last Name</a>', appInputs.lastNameInput === '' ? 'missingValue' : null));
            }
            if (ssnRegex) {
                appErrs.ssnError = true;
                errList.push(generateErrorMessage('<a href="#socialSecurity-field-container">Social Security Number</a>', appInputs.ssnInput === '' ? 'missingValue': null));
            }

            if (errList.length !== 0) {
                const errorObj = {
                    header: 'Problem Processing Your Request.',
                    message: errList,
                    description: 'The following fields and/or sections require your attention:',
                    pushdownCode: 0,
                    color: 'error',
                };
                setIsQuerying(false);
                showPushdown(errorObj);
            }
            return errList;
        };

        if (handlePushdowns().length > 0) {
            return; // exit if there are errors
        }

        if (isFundingToggled()) {
            const now = Date.now();
            const yearAgoDate = new Date(now);
            yearAgoDate.setFullYear(yearAgoDate.getFullYear() - 1);
            const yearAgo = yearAgoDate.getTime();

            callAcquisitionBff<NewApplicationStatusResponse>('application-status', {
                body: {
                    identification: {
                        type: 'SSN',
                        value: appInputs.ssnInput,
                    },
                    lastName: appInputs.lastNameInput,
                    fromDate: yearAgo,
                    toDate: now,
                },
            })
                .then(response => {
                    if (response.status !== 200) {
                        throw { status: response.status };
                    }
                    onNewApiSuccess(response.data);
                })
                .catch(onError)
                .finally(() => setIsQuerying(false));
        } else if (!isFundingToggled()) {
            dynamicRequest<ApplicationStatusResponse>('application-status', {
                body: {
                    ssn: appInputs.ssnInput,
                    lastName: appInputs.lastNameInput,
                },
                requestType: 'POST',
            })
                .then(response => onSuccess(response.data))
                .catch(() => {
                    onError();
                })
                .finally(() => {
                    setIsQuerying(false);
                });
        }
    };

    const button: Button = {
        label: 'Check Status',
        variant: 'solid',
        size: 'medium',
    };

    const errorObject = {
        noStatusCode: props.noStatusErrorCode!,
        apiErrorCode: props.apiErrorCode!,
        processingErrorCode: props.processingErrorCode!,
        mdValidationErrorCode: props.mdValidationErrorCode!,
    };

    const missingInfoObj = {
        author: props.author!,
        isInEditor: props.isInEditor,
        cqPath: props.cqPath,
        ...missingInfo,
    };

    const credProtectionObj = {
        isInEditor: props.isInEditor,
        cqPath: props.cqPath,
        ...creditProtectionInfo,
    };


    useEffect(() => {
        if (cardList?.length < 1) {
            return;
        }

        const applicableCards = cardList.filter((card: ApplicationStatus) => {
            if (isFundingToggled()) {
                // all cards returned from new API are pre-filtered by time via the request
                return true;
            }
            const currentYear = new Date().getFullYear();
            const oneYearBeforeToday = new Date().setFullYear(currentYear - 1);
            const expireDate = new Date(oneYearBeforeToday).setHours(0, 0, 0, 0);
            return new Date(card.ReceivedDate).setHours(0, 0, 0, 0) >= expireDate;
        });
        
        if (applicableCards.length > 0) {
            const cards = applicableCards.slice(0,10).map((card: ApplicationStatus, index) => (
                <div key={`card-${index}`}>
                    <StatusTemplate
                        key={`data-${index}`}
                        status={card.Status}
                        verificationStatus={card.BankAccountVerificationStatus}
                        isNotEnrolled={card.IsNotEnrolledInCreditProtectionOffer}
                        cardName={card.ProductName}
                        applicationCode={card.ReservationNumber}
                        dateReceived={abbreviateDate(card.ReceivedDate)}
                        fundingAccount={card.LastFourBankAccountNumbers}
                        applicationId={card.ApplicationId}
                        errors={errorObject}
                        index={index}
                        isInEditor={props.isInEditor}
                        cqPath={props.cqPath}
                        creditProtection={credProtectionObj}
                        missingInfo={missingInfoObj}
                        wasCardMailed={card.WasCardMailed}
                        mailDate={abbreviateDate(card.MailDate)}
                        arriveDate={abbreviateDate(card.ArrivalDate)}
                        approvedResponseMessage={props.approvedResponseMessage}
                        mailedResponseMessage={props.mailedResponseMessage}
                        declinedResponseMessage={props.declinedResponseMessage}
                        cancelledResponseMessage={props.cancelledResponseMessage}
                        inProcessResponseMessage={props.inProcessResponseMessage}
                        missingResponseMessage={props.missingResponseMessage}
                        approvedHeaderMessage={props.approvedHeaderMessage}
                        mailedHeaderMessage={props.mailedHeaderMessage}
                        declinedHeaderMessage={props.declinedHeaderMessage}
                        cancelledHeaderMessage={props.cancelledHeaderMessage}
                        inProcessHeaderMessage={props.inProcessHeaderMessage}
                        missingHeaderMessage={props.missingHeaderMessage}
                        readyToFundHeaderMessage={props.readyToFundHeaderMessage}
                        readyToFundResponseMessage={props.readyToFundResponseMessage}
                        fundingPageLinkText={props.fundingPageLinkText}
                        fundingPageLinkUrl={props.fundingPageLinkUrl}
                        pendingDecisionLinkText={props.pendingDecisionLinkText}
                        pendingDecisionLinkUrl={props.pendingDecisionLinkUrl}
                        fundingAttempts={card.fundingAttempts}
                        rawApplicationData={card.rawApplicationData}
                        setFilteredCards={setFilteredCards}
                        setAppInputs={setAppInputs}
                        processingErrorCode={props.processingErrorCode}
                        declineLetterFeatureActive={props.declineLetterFeatureActive}
                        declinedLetterUnavailableHeader={props.declinedLetterUnavailableHeader}
                        declinedLetterUnavailableMessage={props.declinedLetterUnavailableMessage}
                        declinedWithinThirtyDaysHeader={props.declinedWithinThirtyDaysHeader}
                        declinedWithinThirtyDaysMessage={props.declinedWithinThirtyDaysMessage}
                        declinedAfterThirtyDaysHeader={props.declinedAfterThirtyDaysHeader}
                        declinedAfterThirtyDaysMessage={props.declinedAfterThirtyDaysMessage}
                        declinedLimitedEsignHeader={props.declinedLimitedEsignHeader}
                        declinedLimitedEsignMessage={props.declinedLimitedEsignMessage}
                        declinedLimitedEsignExperienceFragmentPath={props.declinedLimitedEsignExperienceFragmentPath}
                        declinedLimitedEsignPrintSectionLogo={props.declinedLimitedEsignPrintSectionLogo}
                        declinedLimitedEsignModalTitle={props.declinedLimitedEsignModalTitle}
                        declinedLimitedEsignModalDescription={props.declinedLimitedEsignModalDescription}
                    />
                </div>
            ));
            setFilteredCards(cards);
        } else {
            callNoStatus();
        }
    }, [cardList]);

    const refToPage = useRef(document.body);
    useSessionTimeout(refToPage);
    return (
        <>
            <div id='application-status-form' className='ApplicationStatusForm'>
                <div className={`ApplicationStatusForm__container ${!filteredCards?.length ? 'ApplicationStatusForm__container--form' : ''}`}>
                    {filteredCards?.length ? (
                        <div className='ApplicationStatusForm__formContainer'>
                            <div className='ApplicationStatusForm__topText no-print'>{props.topLineText}</div>
                            <div>
                                {filteredCards}
                            </div>
                        </div>
                    ) : (
                        <Row className='p-4'>
                            <Col xs={6}>
                                <Title tag='h2' verticalBar spanClass='ApplicationStatusForm__description' spanTitle={transformHTMLParserLinks(props.description)}>
                                        {transformHTMLParserLinks(props.title)}
                                </Title>
                                <Row>
                                    <Col xs={6} md={9} className='mt-8'>
                                        <div
                                            id='lastName-field-container'
                                        >
                                            <NameField
                                                alternate
                                                name='Last Name'
                                                className='ApplicationStatusForm__lastNameInput'
                                                label='Last Name'
                                                value={appInputs.lastNameInput}
                                                setValueCallback={(value: string) => {
                                                    setAppInputs(prevState => ({ ...prevState, lastNameInput: value }));
                                                    setAppErrs(prevState => ({ ...prevState, lastNameError: false }))
                                                }}
                                                error={appErrs.lastNameError}
                                                invalidErrorMessage={generateErrorMessage('Last Name')}
                                                requiredMessage={generateErrorMessage('Last Name', 'missingValue')}
                                                useSuccessIcon
                                                showSuccessIcon={!appErrs.lastNameError}
                                            />
                                        </div>
                                        <div
                                            id='socialSecurity-field-container'
                                        >
                                            <SsnField
                                                ref={ssnRef}
                                                className='ApplicationStatusForm__ssnInput'
                                                label='Social Security Number'
                                                value={appInputs.ssnInput}
                                                setValueCallback={(value) => {
                                                    setAppInputs(prevState => ({ ...prevState, ssnInput: value }));
                                                }}
                                                valid={[appErrs.ssnError, (value: boolean) => {
                                                    setAppErrs(prevState => ({ ...prevState, ssnError: !value }));
                                                }]}
                                                required
                                                error={appErrs.ssnError}
                                                showSuccessIcon={!appErrs.ssnError}
                                            />
                                        </div>
                                        <CLButton className='mt-2 [@media(min-width:769px)]:mt-4 !px-6'
                                            {...button}
                                            onClick={() => {
                                                setIsQuerying(true);
                                                queryForStatus();
                                            }}
                                            disabled={isQuerying}>
                                            {button.label}
                                        </CLButton>
                                    </Col>

                                </Row>
                            </Col>
                        </Row>
                    )}
                </div>
                <div className={`ApplicationStatusForm__parsysContainer${props.isInEditor ? '' : '--displayNone'}`}>
                    <div className='aem-Grid aem-Grid--12'>
                        {
                            props.cqItemsOrder?.map((item: string, index: number) => {
                                return (
                                    <div key={`ParsysContainer-${index}`} className='PARSYS'>
                                        <CobGrid isInEditor={props.isInEditor} cqPath={`${props.cqPath}/${item}`} cqItems={getSecChildren(props, item)} cqItemsOrder={props.cqItems[item][':itemsOrder']} />
                                    </div>
                                );
                            })
                        }
                    </div>
                </div>
            </div>
        </>
    );
};

export default ApplicationStatusForm;
